import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, RevealLines } from "@/lib/motion";
import { RefreshCw, Trash2, Mail, Phone, Inbox, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [tab, setTab] = useState("enquiries");
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [a, b] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/newsletter`),
      ]);
      setItems(a.data);
      setSubs(b.data);
    } catch (e) {
      toast.error("Unable to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const removeSub = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/newsletter/${id}`);
      setSubs((prev) => prev.filter((i) => i.id !== id));
      toast.success("Subscriber removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportSubs = () => {
    const csv =
      "email,source,created_at\n" +
      subs.map((s) => `${s.email},${s.source || ""},${s.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yach-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <main data-testid="admin-page" className="pt-28 md:pt-32 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <span className="eyebrow">Studio · Admin</span>
        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <RevealLines
            className="font-display text-[10vw] md:text-[7vw] leading-[0.9] tracking-tighter"
            lines={[<>Studio <em className="hero-italic">inbox.</em></>]}
            stagger={0.1}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="btn-ghost"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-[#D9D4C7]" data-testid="admin-tabs">
          <TabButton active={tab === "enquiries"} onClick={() => setTab("enquiries")} icon={<Inbox className="w-4 h-4" />} label="Enquiries" count={items.length} testid="tab-enquiries" />
          <TabButton active={tab === "subscribers"} onClick={() => setTab("subscribers")} icon={<Users className="w-4 h-4" />} label="Subscribers" count={subs.length} testid="tab-subscribers" />
        </div>
      </section>

      {tab === "enquiries" && (
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24" data-testid="admin-list">
        <Reveal>
          <div className="border-t border-[#D9D4C7]">
            <div className="hidden md:grid grid-cols-12 gap-4 py-4 font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] border-b border-[#D9D4C7]">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Project type</div>
              <div className="col-span-3">Received</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {loading && (
              <div className="py-16 text-center font-grotesk text-sm text-[#4A4A4A]">
                Loading enquiries…
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="py-24 text-center">
                <div className="font-display text-4xl md:text-5xl">No enquiries yet.</div>
                <p className="mt-4 font-grotesk text-sm text-[#4A4A4A]">
                  Submissions from the contact form will appear here.
                </p>
              </div>
            )}

            {!loading && items.map((it) => (
              <div
                key={it.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 border-b border-[#D9D4C7] items-start hover:bg-[#EAE7E0]/40 transition-colors cursor-pointer"
                onClick={() => setSelected(it)}
                data-testid={`admin-row-${it.id}`}
              >
                <div className="md:col-span-3">
                  <div className="font-display text-2xl leading-tight">{it.name}</div>
                  <div className="md:hidden font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] mt-1">
                    {fmt(it.created_at)}
                  </div>
                </div>
                <div className="md:col-span-3 font-grotesk text-sm space-y-1">
                  <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#8C7A6B]" />{it.email}</div>
                  {it.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#8C7A6B]" />{it.phone}</div>}
                </div>
                <div className="md:col-span-2 font-grotesk text-[13px] text-[#4A4A4A]">
                  <div>{it.project_type || "—"}</div>
                </div>
                <div className="md:col-span-3 font-mono-tab text-[12px] text-[#4A4A4A] hidden md:block">
                  {fmt(it.created_at)}
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <button
                    onClick={(e) => { e.stopPropagation(); removeEnquiry(it.id); }}
                    className="p-2 border border-[#D9D4C7] hover:border-[#1A1A1A] transition-colors"
                    aria-label="Delete"
                    data-testid={`admin-delete-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
      )}

      {tab === "subscribers" && (
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24" data-testid="admin-subs">
        <Reveal>
          <div className="flex items-center justify-between mb-6">
            <p className="font-grotesk text-[13px] text-[#4A4A4A]">
              {subs.length} newsletter subscriber{subs.length === 1 ? "" : "s"} — export to CSV for Mailchimp / Resend / any tool.
            </p>
            <button onClick={exportSubs} className="btn-ghost" disabled={subs.length === 0} data-testid="export-subs">
              <span>Export CSV</span>
              <span>↓</span>
            </button>
          </div>

          <div className="border-t border-[#D9D4C7]">
            <div className="hidden md:grid grid-cols-12 gap-4 py-4 font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] border-b border-[#D9D4C7]">
              <div className="col-span-6">Email</div>
              <div className="col-span-3">Source</div>
              <div className="col-span-2">Subscribed</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {loading && (
              <div className="py-16 text-center font-grotesk text-sm text-[#4A4A4A]">Loading…</div>
            )}
            {!loading && subs.length === 0 && (
              <div className="py-24 text-center">
                <div className="font-display text-4xl md:text-5xl">No subscribers yet.</div>
                <p className="mt-4 font-grotesk text-sm text-[#4A4A4A]">
                  Emails from the footer signup form will appear here.
                </p>
              </div>
            )}
            {!loading && subs.map((s) => (
              <div
                key={s.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 py-4 border-b border-[#D9D4C7] items-center"
                data-testid={`sub-row-${s.id}`}
              >
                <div className="md:col-span-6 font-grotesk text-sm flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#8C7A6B]" />{s.email}
                </div>
                <div className="md:col-span-3 font-grotesk text-[12px] uppercase tracking-[0.2em] text-[#4A4A4A]">
                  {s.source || "—"}
                </div>
                <div className="md:col-span-2 font-mono-tab text-[12px] text-[#4A4A4A]">
                  {fmt(s.created_at)}
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <button
                    onClick={() => removeSub(s.id)}
                    className="p-2 border border-[#D9D4C7] hover:border-[#1A1A1A] transition-colors"
                    aria-label="Delete"
                    data-testid={`sub-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-[70] bg-[#1A1A1A]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="admin-detail"
        >
          <div
            className="bg-[#FAF9F6] max-w-2xl w-full p-8 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 w-9 h-9 bg-[#1A1A1A] text-[#FAF9F6] flex items-center justify-center"
              data-testid="admin-detail-close"
            >
              ✕
            </button>
            <span className="eyebrow">Enquiry</span>
            <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[0.95] tracking-tight">
              {selected.name}
            </h3>
            <div className="mt-6 grid grid-cols-2 gap-4 font-grotesk text-sm border-t border-[#D9D4C7] pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-1">Email</div>
                <div className="break-all">{selected.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-1">Phone</div>
                <div>{selected.phone || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-1">Project</div>
                <div>{selected.project_type || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-1">Received</div>
                <div className="font-mono-tab">{fmt(selected.created_at)}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Message</div>
                <div className="leading-[1.7] whitespace-pre-wrap">{selected.message}</div>
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4">
              <a href={`mailto:${selected.email}`} className="btn-sharp" data-testid="admin-reply">
                <span>Reply by email</span>
                <span>→</span>
              </a>
              <button
                onClick={() => removeEnquiry(selected.id)}
                className="btn-ghost"
                data-testid="admin-detail-delete"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function TabButton({ active, onClick, icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-3 font-grotesk text-[11px] uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
        active ? "border-[#1A1A1A] text-[#1A1A1A]" : "border-transparent text-[#4A4A4A] hover:text-[#1A1A1A]"
      }`}
    >
      {icon}
      <span>{label}</span>
      <span className="text-[10px] text-[#8C7A6B]">{count}</span>
    </button>
  );
}
